// Consumer Voice audit rows (API: lib/controllers/consumerVoice projectAudit)
// and the report shapes lib/consumerVoice/audit.js stores (report.version 2
// adds demand, content_opportunities and the objective-aligned scores; v1
// rows lack them, so every consumer guards on presence).

export type ConsumerVoiceStatus = 'pending' | 'ready' | 'error';
export type ConsumerVoiceKind = 'product' | 'brand';

export interface CvQuote {
  text: string;
  url: string;
}

export interface CvTheme {
  theme: string;
  sentiment: 'positive' | 'mixed' | 'negative';
  frequency: 'High' | 'Medium' | 'Low';
  insight: string;
  quotes: CvQuote[];
}

export interface CvSubscores {
  sentiment: number | null;
  advocacy: number | null;
  objection_severity: number | null;
  confidence: number | null;
}

export interface CvSourceUsed {
  key: string;
  signal?: string;
  items?: number;
  posts?: number;
  comments?: number;
  stats?: Record<string, any> | null;
  skipped?: string | null;
  cached?: boolean;
  error?: string | null;
}

export type CvObjectionCategory =
  | 'quality_defect'
  | 'safety'
  | 'value'
  | 'authenticity'
  | 'fit_compatibility'
  | 'usability'
  | 'support'
  | 'other';

export type CvQuestionTopic =
  | 'how_to'
  | 'compatibility'
  | 'specs'
  | 'comparison'
  | 'care_maintenance'
  | 'safety'
  | 'warranty_support'
  | 'other';

export interface CvRelatedQuery {
  query: string;
  value: number | null;
  breakout?: boolean;
  link?: string | null;
  keyword?: string | null;
}

export interface CvDemandTrends {
  primary: string | null;
  // The term the momentum numbers describe: the product term, or — when
  // that scores 0 all year (a full title is too long-tail for Trends) —
  // the compared term with the most interest, usually the brand.
  headline_term?: string | null;
  interest_over_time: Array<{ date: string; value: number }>;
  // Every compared term's line on the explore's shared 0-100 scale
  // (product, brand, category); absent on older analyses.
  series_by_term?: Array<{ term: string; points: Array<{ date: string; value: number }> }>;
  avg_12m: number | null;
  last_90d_avg: number | null;
  prior_avg: number | null;
  query_growth_pct: number | null;
  trend_direction: 'breakout' | 'rising' | 'stable' | 'falling' | 'none' | null;
  comparison_share: Record<string, number | null> | null;
  related_queries: { top: CvRelatedQuery[]; rising: CvRelatedQuery[] };
  geo_interest: Array<{ region: string; code?: string | null; value: number }>;
  question_query_share: number | null;
  problem_query_share: number | null;
  partial?: boolean;
}

export interface CvDemandKeywordPlanner {
  search_volume: number | null;
  search_volume_brand: number | null;
  search_volume_category: number | null;
  monthly_history: Array<{ month: string; searches: number }>;
  query_growth_pct: number | null;
  related_keywords: Array<{ text: string; avg_monthly_searches: number; competition: string | null; competition_index: number | null }>;
  competition_index: number | null;
  question_query_share: number | null;
  problem_query_share: number | null;
}

export interface CvDemandSearchConsole {
  window: { startDate: string; endDate: string; days: number } | null;
  domain: string | null;
  impressions: number;
  clicks: number;
  ctr: number | null;
  position: number | null;
  top_queries: Array<{ query: string; impressions: number; clicks: number; ctr: number; position: number | null; url?: string | null }>;
  pages: Array<{ url: string; impressions: number; clicks: number }>;
  question_query_share: number | null;
  problem_query_share: number | null;
}

export interface CvDemand {
  keywords: string[];
  trends: CvDemandTrends | null;
  keyword_planner: CvDemandKeywordPlanner | null;
  search_console: CvDemandSearchConsole | null;
  youtube: { videos: number; total_views: number; comments: number } | null;
  retailer: {
    review_count: number | null;
    avg_rating: number | null;
    histogram: Record<string, number> | null;
    verified_share: number | null;
    question_count: number | null;
    unanswered_question_count: number | null;
    urls?: Array<{ url: string; vendor: string | null; reviews: number; questions: number; error: string | null }>;
  } | null;
}

export type CvOpportunityType =
  | 'unanswered_question'
  | 'objection'
  | 'problem_query'
  | 'question_query'
  | 'comparison'
  | 'rising_query'
  | 'pdp_gap';

export interface CvContentOpportunity {
  key: string;
  type: CvOpportunityType;
  title: string;
  score: number;
  components: { demand: number; frequency: number; gap: number };
  sources: string[];
  evidence: Array<{ kind: string; source: string | null; text: string; url: string | null; demand_kind?: string; demand_value?: number | null }>;
  // brand roll-up only
  products?: string[];
}

export interface CvComponentBlock {
  components: Record<string, number | null>;
  weights_used: Record<string, number>;
  inputs?: Record<string, any>;
  objective?: string;
  gated_null?: boolean;
  volume_source?: string | null;
  pdp_score_id?: number | null;
}

export interface CvReportMeta {
  scoring_version?: number;
  indices?: { consumer_voice: number | null; demand: number | null; opportunity: number | null };
  sources_used?: CvSourceUsed[];
  inputs?: { discussion_volume?: string | null };
  keywords?: { primary: string | null; terms: string[]; hints?: string[] };
  // Subreddits / keywords found by Google + AI because none were entered.
  discovered?: CvDiscovered | null;
  demand?: CvComponentBlock;
  opportunity?: CvComponentBlock;
  objective_scores?: { trust: CvComponentBlock; education: CvComponentBlock; hype: CvComponentBlock };
  pdp_context?: { score_id: number | null; report_id?: number | null; overall: number | null; clarity: number | null; scored_at: string | null } | null;
}

export interface ConsumerVoiceProductReport {
  version: number;
  kind: 'product';
  subject?: {
    brand_name?: string;
    product_name?: string;
    category?: string;
    url?: string;
    pdp_urls?: Array<{ url: string; hostname: string; brand_site: boolean }>;
  };
  coverage: 'strong' | 'moderate' | 'sparse' | 'none';
  relevant_mention_count: number;
  discussion_volume?: 'high' | 'medium' | 'low' | 'none';
  summary: string;
  themes: CvTheme[];
  praise: Array<{ text: string; quotes: CvQuote[] }>;
  pain_points: Array<{ text: string; severity: 'High' | 'Medium' | 'Low'; quotes: CvQuote[] }>;
  objections: Array<{ text: string; category?: CvObjectionCategory; quotes: CvQuote[] }>;
  unanswered_questions: Array<{ question: string; topic?: CvQuestionTopic; url: string }>;
  comparisons: Array<{ competitor: string; context: string }>;
  personas: string[];
  use_cases: string[];
  copy_bank: Array<{ phrase: string; url: string }>;
  // v1 rows carry `subreddit`; v2 rows carry `site`.
  sources: Array<{ url: string; site?: string; subreddit?: string; title: string }>;
  understanding?: { confusion_share: number; clarity_note: string } | null;
  launch?: { anticipation_share: number; early_sentiment: number } | null;
  demand?: CvDemand | null;
  content_opportunities?: CvContentOpportunity[];
  meta?: CvReportMeta;
}

export interface ConsumerVoiceBrandReport {
  version: number;
  kind: 'brand';
  summary: string;
  cross_product_themes: Array<{ theme: string; sentiment: 'positive' | 'mixed' | 'negative'; products: string[]; insight: string }>;
  strongest_praise: string[];
  recurring_pain_points: string[];
  recurring_questions: string[];
  per_product: Array<{
    audit_id: number;
    desireid: number | null;
    title: string;
    status: string;
    overall_score: number | null;
    demand_index?: number | null;
    opportunity_index?: number | null;
    trust_score?: number | null;
    education_score?: number | null;
    hype_score?: number | null;
    trend_direction?: string | null;
    coverage: string;
    summary: string | null;
    one_line: string | null;
  }>;
  top_opportunities?: CvContentOpportunity[];
  products_analyzed: number;
  products_with_data: number;
  meta?: {
    indices?: { consumer_voice: number | null; demand: number | null; opportunity: number | null };
    objective_scores?: { trust: number | null; education: number | null; hype: number | null };
  };
}

export type ConsumerVoiceReport = ConsumerVoiceProductReport | ConsumerVoiceBrandReport;

// Inputs a run found for itself (no subreddits/keywords were entered).
// Comma-separated, like the cv_subreddits / cv_keywords settings.
export interface CvDiscovered {
  subreddits: string | null;
  keywords: string | null;
  source: 'google+llm' | 'google';
  query: string;
}

export interface ConsumerVoiceAudit {
  id: number;
  kind: ConsumerVoiceKind;
  desireid: number | null;
  subject_brand_id: number | null;
  brand_id: number | null;
  parent_id: number | null;
  url: string | null;
  status: ConsumerVoiceStatus;
  generation_error: string | null;
  overall_score: number | null;
  subscores: CvSubscores | null;
  demand_index: number | null;
  opportunity_index: number | null;
  // Objective-aligned scores (build trust / educate / launch). Absent on
  // rows scored before scoring v2.
  trust_score?: number | null;
  education_score?: number | null;
  hype_score?: number | null;
  source_stats: Record<string, any> | null;
  params: {
    brand_name: string | null;
    product_name: string | null;
    category: string | null;
    sources: string[] | null;
    product_count: number | null;
    progress: { done: number; total: number } | null;
    product_audit_ids: number[] | null;
    discovered?: CvDiscovered | null;
  };
  started_at: string | null;
  scored_at: string | null;
  created_at: string;
  updated_at: string;
  report?: ConsumerVoiceReport | null;
}

// Product settings > Consumer Voice tab payload (GET/PATCH .../settings).
export interface ConsumerVoiceSettings {
  desireid: number;
  cv_subreddits: string | null;
  brand_cv_subreddits: string | null;
  brand_name: string | null;
  effective_subreddits: string | null;
  cv_keywords: string | null;
  brand_cv_keywords: string | null;
  effective_keywords: string | null;
  // The product's Consumer Voice schedule and the plan's cap (lib/analysisSchedule).
  cv_analysis_interval?: string;
  cv_analysis_weekday?: number | null;
  cv_analysis_day_of_month?: number | null;
  max_interval?: string | null;
}

// Human labels for the objective-aligned scores and where they point.
export const CV_OBJECTIVE_SCORES: Array<{
  key: 'trust_score' | 'education_score' | 'hype_score';
  label: string;
  objective: string;
  caption: string;
}> = [
  { key: 'trust_score', label: 'Trust', objective: 'Build trust & credibility', caption: 'Advocacy, sentiment, ratings, trust objections' },
  { key: 'education_score', label: 'Education', objective: 'Educate complex products', caption: 'Understanding, open questions, answered Q&A, page clarity' },
  { key: 'hype_score', label: 'Hype', objective: 'Launch new product', caption: 'Search momentum, recent talk, first impressions' },
];

export const CV_OPPORTUNITY_TYPE_LABELS: Record<CvOpportunityType, string> = {
  unanswered_question: 'Unanswered question',
  objection: 'Objection',
  problem_query: 'Problem search',
  question_query: 'Question search',
  comparison: 'Comparison',
  rising_query: 'Rising search',
  pdp_gap: 'Page gap',
};

export const cvSourceLabel = (s: { site?: string; subreddit?: string }) => {
  if (s.site) return s.site.replace(/^reddit:/, '');
  if (s.subreddit) return `r/${s.subreddit}`;
  return '';
};

// A pending row older than this is a dead worker, not a running analysis
// (mirrors the API's stale-recovery cutoff; longer than PDP's 15 min
// because a run includes the Reddit retry ladder).
export const CV_PENDING_STALE_MS = 60 * 60 * 1000;

export const isCvRunning = (a: Pick<ConsumerVoiceAudit, 'status' | 'updated_at'> | null | undefined) =>
  !!a && a.status === 'pending' && Date.now() - new Date(a.updated_at).getTime() < CV_PENDING_STALE_MS;

export const cvSubjectLabel = (a: ConsumerVoiceAudit) =>
  a.kind === 'brand'
    ? a.params.brand_name || `Brand ${a.subject_brand_id}`
    : a.params.product_name || a.url || `Product ${a.desireid}`;

// "Auto-discovered" note when the run found its own subreddits / keywords.
export const cvDiscoveredLine = (meta: CvReportMeta | null | undefined): string => {
  const d = meta && meta.discovered;
  if (!d) return '';
  const parts: string[] = [];
  if (d.subreddits) parts.push(`communities ${d.subreddits.split(', ').map((n) => `r/${n}`).join(', ')}`);
  if (d.keywords) parts.push(`search terms ${d.keywords}`);
  return parts.length ? `Auto-discovered from Google + Reddit: ${parts.join(' · ')}` : '';
};
